/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect } from "react";
import dayjs from "dayjs";
import { Else, If, Then } from "react-if";
import { ListLoader, Text, theme } from "@nordcloud/gnui";
import { useGetEventsViewQuery, EventsSortFields } from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import { useQueryState } from "~/hooks";
import { isEmpty, isNotEmpty } from "~/tools";
import {
  EventsGridHeader,
  EventsTableHeaderRow,
  EventsTableInner,
  EventsTableScrollContainer,
} from "../styled";
import { QueryGetEvents } from "./constants";
import { prepareEventsFilterBy } from "./eventFilterBuilders";
import { EventItem } from "./EventItem";
import { EventsLoader } from "./EventLoader";

type Props = {
  onResultsCountChange?: (count: number) => void;
};

export function EventsTable({ onResultsCountChange }: Props) {
  const { queryState } = useQueryState<QueryGetEvents>();

  const sortInput = {
    field: queryState.sortField as EventsSortFields,
    order: queryState.order,
  };

  const { data, previousData, loading } = useGetEventsViewQuery({
    variables: {
      page: Number(queryState.page ?? "1"),
      limit: Number(queryState.limit),
      sort: sortInput,
      filter: {
        filterBy: prepareEventsFilterBy(queryState),
        phrase: isNotEmpty(queryState.searchPhrase?.trim() || "")
          ? queryState.searchPhrase
          : undefined,
      },
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "network-only",
  });

  const count = loading
    ? previousData?.eventsView.pageInfo?.count ??
      data?.eventsView.pageInfo?.count ??
      0
    : data?.eventsView.pageInfo?.count ?? 0;
  const events = data?.eventsView?.result ?? [];

  useEffect(() => {
    onResultsCountChange?.(count);
  }, [count, onResultsCountChange]);

  const wrongRangeInput = dayjs(queryState.startDateUTC).isAfter(
    queryState.endDateUTC
  );

  return (
    <>
      <LoaderWrap
        loading={loading}
        Component={() => (
          <ListLoader>
            <EventsLoader />
          </ListLoader>
        )}
      >
        <If condition={isEmpty(events)}>
          <Then>
            <If condition={wrongRangeInput}>
              <Then>
                <NoData
                  hasIcon
                  message={
                    "The date range is incorrect. Please ensure that the 'startDate' and 'endDate' parameters in the link are correctly specified."
                  }
                />
              </Then>
              <Else>
                <NoData hasIcon message={"There are no Events."} />
              </Else>
            </If>
          </Then>
          <Else>
            <>
              <EventsTableScrollContainer>
                <EventsTableInner>
                  <EventsTableHeaderRow>
                    <EventsGridHeader hasPadding>
                      <div />
                      <div />
                      <Text
                        size="sm"
                        weight="medium"
                        m={0}
                        color={theme.color.text.text02}
                      >
                        Event Name
                      </Text>
                      <Text
                        size="sm"
                        weight="medium"
                        m={0}
                        color={theme.color.text.text02}
                      >
                        Start Time (local time)
                      </Text>
                      <Text
                        size="sm"
                        weight="medium"
                        m={0}
                        color={theme.color.text.text02}
                      >
                        End Time (local time)
                      </Text>
                      <Text
                        size="sm"
                        weight="medium"
                        m={0}
                        color={theme.color.text.text02}
                      >
                        Event Duration
                      </Text>
                      <div />
                    </EventsGridHeader>
                  </EventsTableHeaderRow>
                  {events.map((event) => (
                    <EventItem key={event.id} event={event} />
                  ))}
                </EventsTableInner>
              </EventsTableScrollContainer>
              <Pagination count={count} />
            </>
          </Else>
        </If>
      </LoaderWrap>
    </>
  );
}
